import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../contexts/AuthContext';
import { ACAO, GRAV, ORIGEM, ORDEM_ORIGENS, dataHora, dec, horasCpu, quandoTexto } from '../components/analiseBanco/comum';

// Relatorios para imprimir / salvar em PDF (fundo claro, sem menu):
//   /ti/analise-banco/relatorio/diretoria — a resposta em 30 segundos, sem SQL;
//   /ti/analise-banco/relatorio/micro     — dossie para a Microuniverso, numerado (MU-01...);
//   /ti/analise-banco/relatorio/nos       — o que corrigir do nosso lado (CT-01...).

const TITULOS = {
  diretoria: 'Banco de dados do ERP: o que está deixando o sistema lento',
  micro: 'Dossiê para a Microuniverso: pontos de desempenho no banco do ERP',
  nos: 'Pontos de desempenho a corrigir do nosso lado',
};

function textoDoDossie(d, titulo) {
  const linhas = [titulo, `Gerado em ${dataHora(d.geradoEm)} · dados do servidor desde ${dataHora(d.servidorDesde)}`, ''];
  d.itens.forEach((i) => {
    linhas.push(`${i.codigo} — ${i.titulo}`);
    linhas.push(`Impacto: ${i.impacto}`);
    linhas.push(`Quando: ${quandoTexto(i.quando)}`);
    linhas.push(`Onde: ${i.onde.join(' · ')} (${i.tecnico})`);
    linhas.push(`O que acontece: ${i.oque}`);
    linhas.push(`O que pedimos: ${i.pedido}`);
    (i.diagnostico || []).filter((x) => x.sugestao).forEach((x) => linhas.push(`  • ${x.titulo}: ${x.sugestao}`));
    (i.evidencias || []).forEach((e) => linhas.push(`  - ${e}`));
    linhas.push('');
  });
  return linhas.join('\n');
}

function Diretoria({ resumo, problemas }) {
  const porOrigem = Object.fromEntries((resumo.origens || []).map((o) => [o.origem, Number(o.cpu_ms)]));
  const total = Object.values(porOrigem).reduce((s, v) => s + v, 0) || 1;
  const micro = problemas.itens.filter((p) => p.acao === 'micro').slice(0, 6);
  const nos = problemas.itens.filter((p) => p.acao === 'nos').slice(0, 6);
  const Lista = ({ itens }) => (
    <ol className="ab-rel__lista">
      {itens.map((p) => (
        <li key={p.id}>
          <strong>{p.tituloCurto || p.titulo}</strong> <span className="ab-rel__num">{p.numeroTexto} {p.unidade}</span>
          <br /><span className="ab-rel__mudo">{p.tipo === 'deadlock' ? quandoTexto(p.quando, { curto: true }) : p.oque}</span>
        </li>
      ))}
    </ol>
  );
  return (
    <>
      <p className="ab-rel__manchete">{problemas.manchete}</p>
      <div className="ab-rel__kpis">
        {problemas.kpis.map((k) => <div key={k.rotulo}><strong>{k.valor}</strong><span>{k.rotulo}</span><small>{k.detalhe}</small></div>)}
      </div>
      <h2>Quem usa o processador do servidor</h2>
      <div className="ab-rel__seg">
        {ORDEM_ORIGENS.filter((o) => porOrigem[o]).map((o) => (
          <div key={o} style={{ width: `${(100 * porOrigem[o]) / total}%`, background: ORIGEM[o].corImpressao || undefined }} className={`ab-rel__seg--${o}`}>
            {(100 * porOrigem[o]) / total >= 9 ? `${ORIGEM[o].rotulo} ${dec((100 * porOrigem[o]) / total, 0)}%` : ''}
          </div>
        ))}
      </div>
      <p className="ab-rel__mudo">
        {ORDEM_ORIGENS.filter((o) => porOrigem[o]).map((o) => `${ORIGEM[o].rotulo}: ${dec((100 * porOrigem[o]) / total, 1)}% (${horasCpu(porOrigem[o])})`).join(' · ')}
      </p>
      <div className="ab-rel__duas">
        <section><h2>{ACAO.micro.titulo}</h2><Lista itens={micro} /></section>
        <section><h2>{ACAO.nos.titulo}</h2><Lista itens={nos} /></section>
      </div>
    </>
  );
}

function Dossie({ d }) {
  return (
    <>
      <div className="ab-rel__kpis">
        {d.kpis.map((k) => <div key={k.rotulo}><strong>{k.valor}</strong><span>{k.rotulo}</span><small>{k.detalhe}</small></div>)}
      </div>
      {d.para === 'micro' && (
        <p className="ab-rel__mudo">
          Configuração atual: estatísticas automáticas no BDENTER {d.configuracao.estatistica || '—'} · MAXDOP {d.configuracao.maxdop} ·
          limite de custo para paralelismo {d.configuracao.limiteCusto} · jobs no SQL Agent: {d.configuracao.jobs || '—'} ·
          BDENTER com {d.configuracao.tamanhoBdenterGb} GB de dados.
        </p>
      )}
      {d.itens.map((i) => (
        <article key={i.codigo} className="ab-rel__item">
          <header>
            <span className="ab-rel__codigo">{i.codigo}</span>
            <h3>{i.titulo}</h3>
            <span className="ab-rel__grav">{GRAV[i.gravidade]?.rotulo}</span>
          </header>
          <dl>
            <div><dt>Impacto</dt><dd>{i.impacto}</dd></div>
            <div><dt>Quando</dt><dd>{quandoTexto(i.quando)}</dd></div>
            <div><dt>Onde</dt><dd>{i.onde.join(' · ')}<br /><code>{i.tecnico}</code></dd></div>
            <div><dt>O que acontece</dt><dd>{i.oque}</dd></div>
            <div><dt>O que pedimos</dt><dd><strong>{i.pedido}</strong></dd></div>
          </dl>
          {i.consultaId && !i.diagnostico && (
            <div className="ab-rel__bloco ab-rel__lendo" role="status">
              <span className="ab-rel__girando" aria-hidden="true" />
              {i.erroDiagnostico ? 'Não foi possível ler o plano desta consulta.' : 'Lendo o plano de execução no servidor para montar o diagnóstico…'}
            </div>
          )}
          {i.diagnostico?.length > 0 && (
            <div className="ab-rel__bloco">
              <h4>Diagnóstico pelo plano de execução</h4>
              <ul>{i.diagnostico.map((x, n) => <li key={n}><strong>{x.titulo}.</strong> {x.sugestao}{x.evidencia?.length ? <><br /><code>{x.evidencia.join(' · ')}</code></> : null}</li>)}</ul>
            </div>
          )}
          {i.evidencias?.length > 0 && (
            <div className="ab-rel__bloco">
              <h4>Exemplos</h4>
              <ul>{i.evidencias.map((e, n) => <li key={n}>{e}</li>)}</ul>
            </div>
          )}
        </article>
      ))}
    </>
  );
}

// Esqueleto do relatorio: mostra onde o conteudo vai entrar enquanto carrega.
function Esqueleto() {
  return (
    <div className="ab-rel__esqueleto" role="status" aria-label="Carregando o relatório">
      <div className="ab-rel__esq ab-rel__esq--linha" style={{ width: '70%' }} />
      <div className="ab-rel__esq-kpis">{[0, 1, 2].map((i) => <div key={i} className="ab-rel__esq ab-rel__esq--kpi" />)}</div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="ab-rel__esq-item">
          <div className="ab-rel__esq ab-rel__esq--titulo" />
          <div className="ab-rel__esq ab-rel__esq--linha" />
          <div className="ab-rel__esq ab-rel__esq--linha" style={{ width: '85%' }} />
          <div className="ab-rel__esq ab-rel__esq--linha" style={{ width: '60%' }} />
        </div>
      ))}
    </div>
  );
}

export default function AnaliseBancoRelatorio() {
  const { tipo } = useParams();
  const navigate = useNavigate();
  const t = ['diretoria', 'micro', 'nos'].includes(tipo) ? tipo : 'diretoria';
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [segundos, setSegundos] = useState(0);

  // Em duas etapas: o dossie chega rapido (sem os planos) e o diagnostico de
  // cada consulta vem depois, um por vez, preenchendo o item na tela.
  useEffect(() => {
    let valido = true;
    setDados(null); setErro('');
    const pedido = t === 'diretoria'
      ? Promise.all([axios.get(`${API}/analise-banco/resumo`), axios.get(`${API}/analise-banco/problemas`)]).then(([r, p]) => ({ resumo: r.data.data, problemas: p.data.data }))
      : axios.get(`${API}/analise-banco/dossie`, { params: { para: t, semDiagnostico: 1 } }).then((r) => ({ dossie: r.data.data }));
    pedido.then(async (d) => {
      if (!valido) return;
      setDados(d);
      if (!d.dossie) return;
      for (const item of d.dossie.itens.filter((i) => i.consultaId && !i.diagnostico)) {
        let diagnostico = null; let falhou = false;
        try {
          const r = await axios.get(`${API}/analise-banco/consultas/${item.consultaId}/diagnostico`);
          diagnostico = r.data.data.itens.map((x) => ({ titulo: x.titulo, sugestao: x.sugestao, quem: x.quem, evidencia: x.evidencia?.slice(0, 2) || [] }));
        } catch (e) { falhou = true; }
        if (!valido) return;
        setDados((atual) => ({
          ...atual,
          dossie: { ...atual.dossie, itens: atual.dossie.itens.map((x) => (x.codigo === item.codigo ? { ...x, diagnostico: falhou ? [] : diagnostico, erroDiagnostico: falhou } : x)) },
        }));
      }
    }).catch((e) => { if (valido) setErro(e.response?.data?.message || 'Não foi possível montar o relatório.'); });
    return () => { valido = false; };
  }, [t]);

  // Relogio da espera: mostra que esta andando, nao travado.
  const pendentes = dados?.dossie ? dados.dossie.itens.filter((i) => i.consultaId && !i.diagnostico).length : 0;
  const totalPlanos = dados?.dossie ? dados.dossie.itens.filter((i) => i.consultaId).length : 0;
  const carregando = !dados || pendentes > 0;
  useEffect(() => {
    if (!carregando || erro) { setSegundos(0); return undefined; }
    const ini = Date.now();
    const id = setInterval(() => setSegundos(Math.floor((Date.now() - ini) / 1000)), 1000);
    return () => clearInterval(id);
  }, [carregando, erro]);

  const copiar = async () => {
    if (!dados?.dossie) return;
    try { await navigator.clipboard.writeText(textoDoDossie(dados.dossie, TITULOS[t])); setCopiado(true); setTimeout(() => setCopiado(false), 2500); } catch (e) { /* sem permissao de area de transferencia */ }
  };

  return (
    <div className="ab-rel">
      <div className="ab-rel__barra">
        <button type="button" onClick={() => navigate('/ti/analise-banco')}><i className="bi bi-arrow-left" /> Voltar para a análise</button>
        <div>
          {carregando && !erro && (
            <span className="ab-rel__status" role="status">
              <span className="ab-rel__girando" aria-hidden="true" />
              {!dados ? 'Montando o relatório…' : `Lendo os planos de execução: ${totalPlanos - pendentes} de ${totalPlanos}`}
              {segundos >= 2 && <small> · {segundos}s</small>}
            </span>
          )}
          {dados?.dossie && <button type="button" onClick={copiar} disabled={pendentes > 0} title={pendentes > 0 ? 'Aguarde terminar de ler os planos' : ''}><i className={`bi ${copiado ? 'bi-check2' : 'bi-clipboard'}`} /> {copiado ? 'Copiado' : 'Copiar como texto'}</button>}
          <button type="button" className="ab-rel__primario" onClick={() => window.print()} disabled={carregando}
            title={carregando ? 'Aguarde o relatório terminar de carregar' : ''}><i className="bi bi-printer" /> Imprimir / salvar PDF</button>
        </div>
      </div>
      {dados && pendentes > 0 && <div className="ab-rel__progresso" aria-hidden="true"><div style={{ width: `${(100 * (totalPlanos - pendentes)) / Math.max(1, totalPlanos)}%` }} /></div>}
      {!dados && !erro && <div className="ab-rel__progresso ab-rel__progresso--indeterminado" aria-hidden="true"><div /></div>}
      <main className="ab-rel__folha">
        <header className="ab-rel__cab">
          <span>Centrão · TI · Análise do Banco de Dados</span>
          <h1>{TITULOS[t]}</h1>
          {dados && <p className="ab-rel__mudo">
            Dados do próprio SQL Server (192.168.0.55), copiados em {dataHora(dados.dossie?.copiadoEm || dados.resumo?.coletas?.cache?.iniciada_em)};
            processador acumulado desde {dataHora(dados.dossie?.servidorDesde || dados.resumo?.coletas?.cache?.sql_ligado_desde)}; deadlocks dos últimos 90 dias.
          </p>}
        </header>
        {erro && <p className="ab-rel__erro">{erro}</p>}
        {!dados && !erro && <Esqueleto />}
        {dados?.problemas && <Diretoria resumo={dados.resumo} problemas={dados.problemas} />}
        {dados?.dossie && <Dossie d={dados.dossie} />}
        <footer className="ab-rel__rodape">
          Os horários das consultas do ERP passam a existir com a coleta a cada 15 minutos. Índice sugerido é ponto de partida: precisa ser avaliado antes de criar.
        </footer>
      </main>
    </div>
  );
}
